import { css } from '@emotion/react';
import React, { useEffect } from 'react';

import {
  trackApplePayImpressionInGTM,
  trackGooglePayImpressionInGTM,
} from '@/analytics/gtm';
import { V1DiscountDetail } from '@/api/clients/ohip';
import { MembershipCard } from '@/booking-membership/components/MembershipCard';
import { getDisloyaltyMembershipPrice } from '@/booking-membership/utils/get-disloyalty-membership-price';
import { PaymentTime } from '@/booking/interfaces/PaymentTime.interface';
import {
  AdditionalInfoFormFields,
  createAdditionalInfoFormValidationSchema,
} from '@/booking/validators/additional-info-form.validator';
import { CurrencyCode } from '@/finance/interfaces';
import { useTranslation } from '@/i18n';
import { useOneClickPaymentProvider } from '@/payments';
import { Form } from '@/ui/form';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { Heading } from '@/ui/typography';

import AccessibleRoomCheckbox from './AccessibleRoomCheckbox.component';
import AdditionalChildBedOrCotInputs from './AdditionalChildBedOrCotInputs.component';
import FlexyTimeInputs from './FlexyTimeInputs.component';
import PaymentMethodButtons from './PaymentMethodButtons.component';
import SpecialRequestsTextInput from './SpecialRequestsTextInput.component';

interface AdditionalInfoFormProps {
  onSubmit: (
    values: AdditionalInfoFormFields,
    hasRequestedOneClickCheckout: boolean
  ) => void;
  initialValues: Partial<AdditionalInfoFormFields>;
  isChildBedSelectable: boolean;
  isFlexyTimeAvailable: boolean;
  paymentTime: PaymentTime;
  currencyCode: string;
  countryCode: string;
  addOns: React.ReactNode;
  discountDetail: V1DiscountDetail;
  displayAccessibleRoomCheckbox: boolean;
  hotelReferenceId: string;
}

const initialValues: AdditionalInfoFormFields = {
  specialRequests: '',
  checkInTime: undefined,
  checkOutTime: undefined,
  childBed: [],
  childCot: [],
  accessRequirements: [],
};

export const AdditionalInfoForm = (props: AdditionalInfoFormProps) => {
  const { t: additionalInfoTranslation } = useTranslation(
    'additionalInformation'
  );
  const { t: commonTranslation } = useTranslation('common');
  const validationSchema =
    createAdditionalInfoFormValidationSchema(commonTranslation);

  const { forms, componentProperties } = useTheme();

  // Show membership card if this is a membership rate code
  const showMembershipCard = props.discountDetail.membershipRateCode;
  const membershipPrice = getDisloyaltyMembershipPrice(
    props.currencyCode as CurrencyCode
  );

  const { supportedWallet: availableOneClickPaymentProvider } =
    useOneClickPaymentProvider({
      country: props.countryCode,
      currency: props.currencyCode,
    });

  useEffect(() => {
    switch (availableOneClickPaymentProvider) {
      case 'applePay':
        trackApplePayImpressionInGTM();
        break;
      case 'googlePay':
        trackGooglePayImpressionInGTM();
        break;
    }
  }, [availableOneClickPaymentProvider]);

  return (
    <Form
      onSubmit={(values) =>
        // proceeds to card payment by default
        props.onSubmit(values, false)
      }
      defaultValues={{ ...initialValues, ...props.initialValues }}
      validationSchema={validationSchema}
      aria-label={additionalInfoTranslation('formAriaLabel')}
    >
      {props.isFlexyTimeAvailable && (
        <div>
          <FlexyTimeInputs />
          <Spacer s="xs" />
        </div>
      )}
      <Heading
        as="h6"
        style="heading6"
        css={css({
          ...forms.heading,
          ['@media all and (max-width: 730px)']: forms.heading?.mobile,
        })}
      >
        {additionalInfoTranslation('specialRequests.title')}
      </Heading>
      <Spacer s="xs" />
      <SpecialRequestsTextInput hotelReferenceId={props.hotelReferenceId} />
      <Spacer s="m" />

      {showMembershipCard && membershipPrice && (
        <>
          <MembershipCard
            amountSaved={props.discountDetail.amountSaved}
            membershipPrice={membershipPrice}
          />
          <Spacer s="m" />
        </>
      )}

      {props.isChildBedSelectable && (
        <div>
          <AdditionalChildBedOrCotInputs />
          <Spacer s="l" />
        </div>
      )}

      {props.displayAccessibleRoomCheckbox && (
        <>
          <AccessibleRoomCheckbox />
          <Spacer s="xl" />
        </>
      )}

      {props.addOns}

      <PaymentMethodButtons
        availableOneClickPaymentProvider={availableOneClickPaymentProvider}
        paymentTime={props.paymentTime}
        onSubmit={props.onSubmit}
        css={{
          '@media all and (max-width: 730px)':
            componentProperties.requestsPage?.paymentMethods?.mobile,
        }}
      />
    </Form>
  );
};
